/**
 * Issue #440 — company names → tickers for the Morning Briefing stock watchlist.
 *
 * ── What was broken ────────────────────────────────────────────────────────
 * The Briefing Builder's ticker box normalized whatever she typed with
 * `t.toUpperCase().replace(/[^A-Z.]/g, "")`. That is not a resolver, it is a
 * character filter: "amazon" became the literal string "AMAZON" and was saved.
 * The dispatcher then interpolated it into the prompt ("Check today's price and
 * movement for these tickers: AMAZON"), web search found no price for a string
 * that is not a symbol, and the no-fabrication rule correctly refused to invent
 * one — so the stocks line silently did not appear. No error, no log, nothing
 * in Sentry. From her side the feature simply never worked.
 *
 * The Aug 1 diagnosis found two real saved values, and they are two different
 * bugs wearing the same coat:
 *   - "AMAZON"  — a company that IS listed. This one is translatable.
 *   - "SEPHORA" — a company that is not listed at all (LVMH owns it). No amount
 *                 of translating fixes it; the only honest answer is to say so.
 *
 * ── Why a local table and not a Claude call ────────────────────────────────
 * The issue left the mechanism to the builder. This is a static table, and the
 * reasoning is worth writing down because "just ask Claude" is the reflex:
 *
 *  1. It is instant. This box commits on blur AND on Enter. A network round
 *     trip there needs a loading state, an error state, and a race guard
 *     between the blur-commit and the 800ms debounced persist — three new
 *     failure modes in a chip input, to answer a question whose answer does
 *     not change.
 *  2. It costs nothing. CLAUDE.md's ANTHROPIC API BUDGET section exists because
 *     CI drained the shared wallet out from under production once already. A
 *     per-keystroke-ish Claude call is small, but "small and recurring" is
 *     exactly the shape that got us there.
 *  3. It is testable. A vitest assertion can pin "amazon → AMZN" forever. It
 *     cannot pin a model's answer.
 *  4. Coverage is the real objection, and it is answerable: moms type
 *     household names. The table below is the consumer-facing large-cap
 *     universe plus the index funds people actually name out loud.
 *
 * ── Unknown input: typed as a ticker, or refused ───────────────────────────
 * Kasandra's call, Sep 13 2026: never save a string Kin can't resolve. A
 * symbol the table doesn't know is accepted ONLY when she typed it the way a
 * ticker is written, in capitals ("ACME", "$ACME"). A real small cap or a new
 * listing still goes in; a lowercase typo ("aaple") is refused with a hint to
 * type the ticker in capitals, instead of saving AAPLE and quietly finding no
 * price every morning.
 *
 * ── Capitals also mean "this exact ticker" ───────────────────────────────────
 * Some nicknames are also someone else's real ticker: "coke" is Coca-Cola to
 * her, but COKE is Coca-Cola Consolidated; "ring" is Amazon's doorbell, RING a
 * gold-miners fund; "dow" is the Dow Jones, DOW is Dow Inc. Typed in capitals,
 * nicknames are skipped and the ticker is taken at its word. Real company names
 * still resolve in capitals ("NIKE" is NKE, not the non-ticker NIKE), because
 * old saved values were uppercased by the previous character filter.
 */

export interface ResolvedTicker {
  /** The symbol actually saved and sent to the fetcher. Always uppercase. */
  symbol: string;
  /** What she sees on the chip. Equal to `symbol` when we have no better name. */
  display: string;
}

export type TickerResolution =
  | { ok: true; symbol: string; display: string }
  | { ok: false; message: string };

interface CompanyEntry {
  symbol: string;
  display: string;
  /** Extra things she might type. The display name is always a key already. */
  aliases?: string[];
}

// U+0300–U+036F, the combining diacritical marks NFD splits an accent into.
// Built from escapes rather than written as a literal character class: the
// literal version renders as an empty-looking `[]` in most editors, which is an
// irresistible thing to "clean up".
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");

/**
 * Lookup key: lowercase, letters and digits only. "Coca-Cola", "coca cola" and
 * "CocaCola" all collapse to `cocacola`, which is the point — she is typing a
 * company from memory, not filling in a form.
 *
 * Accents are folded first, not stripped. Without the NFD pass "Nestlé" loses
 * the é entirely and keys as `nestl`, which then LOOKS like a five-letter
 * symbol and gets saved as the ticker NESTL — the #440 bug, reintroduced by a
 * regex. Same for "Estée Lauder".
 */
export function nameKey(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** 1-5 letters, with an optional class suffix (BRK.B). */
const SYMBOL_SHAPE = /^[A-Z]{1,5}(\.[A-Z]{1,2})?$/;

/** Strips everything a symbol cannot contain, including a leading "$AAPL". */
function symbolCandidate(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z.]/g, "");
}

const COMPANIES: CompanyEntry[] = [
  // ── Tech & internet ──────────────────────────────────────────────────────
  { symbol: "AAPL", display: "Apple" },
  { symbol: "AMZN", display: "Amazon", aliases: ["amazonprime", "primevideo", "wholefoods", "audible", "twitch", "zappos", "ring"] },
  { symbol: "GOOGL", display: "Alphabet", aliases: ["google", "youtube", "gmail", "waymo", "fitbit", "nest"] },
  { symbol: "MSFT", display: "Microsoft", aliases: ["xbox", "linkedin", "office365"] },
  { symbol: "META", display: "Meta", aliases: ["facebook", "instagram", "whatsapp", "threads", "oculus"] },
  { symbol: "NVDA", display: "Nvidia" },
  { symbol: "TSLA", display: "Tesla" },
  { symbol: "NFLX", display: "Netflix" },
  { symbol: "DIS", display: "Disney", aliases: ["waltdisney", "disneyplus", "hulu", "espn", "pixar", "marvel"] },
  { symbol: "SPOT", display: "Spotify" },
  { symbol: "UBER", display: "Uber", aliases: ["ubereats"] },
  { symbol: "LYFT", display: "Lyft" },
  { symbol: "ABNB", display: "Airbnb" },
  { symbol: "DASH", display: "DoorDash" },
  { symbol: "ETSY", display: "Etsy" },
  { symbol: "EBAY", display: "eBay" },
  { symbol: "SHOP", display: "Shopify" },
  { symbol: "PINS", display: "Pinterest" },
  { symbol: "SNAP", display: "Snap", aliases: ["snapchat"] },
  { symbol: "RBLX", display: "Roblox" },
  { symbol: "EA", display: "Electronic Arts" },
  { symbol: "TTWO", display: "Take-Two", aliases: ["rockstargames", "zynga"] },
  { symbol: "ADBE", display: "Adobe", aliases: ["photoshop"] },
  { symbol: "CRM", display: "Salesforce", aliases: ["slack"] },
  { symbol: "ORCL", display: "Oracle" },
  { symbol: "IBM", display: "IBM" },
  { symbol: "INTC", display: "Intel" },
  { symbol: "AMD", display: "AMD", aliases: ["advancedmicrodevices"] },
  { symbol: "QCOM", display: "Qualcomm" },
  { symbol: "CSCO", display: "Cisco" },
  { symbol: "PYPL", display: "PayPal", aliases: ["venmo"] },
  { symbol: "ZM", display: "Zoom" },
  { symbol: "PLTR", display: "Palantir" },
  { symbol: "CRWD", display: "CrowdStrike" },
  { symbol: "NOW", display: "ServiceNow" },
  { symbol: "TXN", display: "Texas Instruments" },
  { symbol: "MU", display: "Micron" },
  { symbol: "AVGO", display: "Broadcom" },
  { symbol: "SONY", display: "Sony", aliases: ["playstation"] },
  { symbol: "NTDOY", display: "Nintendo" },
  { symbol: "DUOL", display: "Duolingo" },
  { symbol: "MTCH", display: "Match Group", aliases: ["tinder", "hinge"] },
  { symbol: "BMBL", display: "Bumble" },
  { symbol: "ROKU", display: "Roku" },
  { symbol: "COIN", display: "Coinbase" },
  { symbol: "HOOD", display: "Robinhood" },
  { symbol: "SOFI", display: "SoFi" },

  // ── Retail & apparel ─────────────────────────────────────────────────────
  { symbol: "WMT", display: "Walmart", aliases: ["sams", "samsclub"] },
  { symbol: "TGT", display: "Target" },
  { symbol: "COST", display: "Costco" },
  { symbol: "HD", display: "Home Depot" },
  { symbol: "LOW", display: "Lowe's" },
  { symbol: "TJX", display: "TJX", aliases: ["tjmaxx", "marshalls", "homegoods", "sierra"] },
  { symbol: "ROST", display: "Ross Stores", aliases: ["ross", "rossdressforless"] },
  { symbol: "DG", display: "Dollar General" },
  { symbol: "DLTR", display: "Dollar Tree", aliases: ["familydollar"] },
  { symbol: "BJ", display: "BJ's Wholesale" },
  { symbol: "KR", display: "Kroger" },
  { symbol: "ACI", display: "Albertsons", aliases: ["safeway", "vons", "jewelosco"] },
  { symbol: "SFM", display: "Sprouts" },
  { symbol: "WBA", display: "Walgreens" },
  { symbol: "CVS", display: "CVS" },
  { symbol: "ULTA", display: "Ulta Beauty", aliases: ["ulta"] },
  { symbol: "BBWI", display: "Bath & Body Works" },
  { symbol: "VSCO", display: "Victoria's Secret" },
  { symbol: "GAP", display: "Gap", aliases: ["oldnavy", "bananarepublic", "athleta"] },
  { symbol: "ANF", display: "Abercrombie & Fitch", aliases: ["abercrombie", "hollister"] },
  { symbol: "AEO", display: "American Eagle", aliases: ["aerie"] },
  { symbol: "URBN", display: "Urban Outfitters", aliases: ["anthropologie", "freepeople", "nuuly"] },
  { symbol: "LULU", display: "Lululemon" },
  { symbol: "NKE", display: "Nike", aliases: ["jordan", "converse"] },
  { symbol: "ADDYY", display: "Adidas" },
  { symbol: "UAA", display: "Under Armour" },
  { symbol: "DECK", display: "Deckers", aliases: ["ugg", "uggs", "hoka"] },
  { symbol: "CROX", display: "Crocs", aliases: ["heydude"] },
  { symbol: "SKX", display: "Skechers" },
  { symbol: "BIRK", display: "Birkenstock" },
  { symbol: "ONON", display: "On Holding", aliases: ["onrunning", "oncloud"] },
  { symbol: "VFC", display: "VF Corp", aliases: ["vans", "northface", "thenorthface", "timberland"] },
  { symbol: "RL", display: "Ralph Lauren" },
  { symbol: "PVH", display: "PVH", aliases: ["calvinklein", "tommyhilfiger"] },
  { symbol: "TPR", display: "Tapestry", aliases: ["coach", "katespade"] },
  { symbol: "CPRI", display: "Capri", aliases: ["michaelkors", "versace", "jimmychoo"] },
  { symbol: "LEVI", display: "Levi's", aliases: ["levistrauss"] },
  { symbol: "CHWY", display: "Chewy" },
  { symbol: "W", display: "Wayfair" },
  { symbol: "RH", display: "RH", aliases: ["restorationhardware"] },
  { symbol: "WSM", display: "Williams-Sonoma", aliases: ["potterybarn", "westelm"] },
  { symbol: "BBY", display: "Best Buy" },
  { symbol: "DKS", display: "Dick's Sporting Goods", aliases: ["dicks"] },
  { symbol: "ASO", display: "Academy Sports" },
  { symbol: "FIVE", display: "Five Below" },
  { symbol: "M", display: "Macy's", aliases: ["bloomingdales"] },
  { symbol: "JWN", display: "Nordstrom" },
  { symbol: "KSS", display: "Kohl's" },
  { symbol: "BURL", display: "Burlington" },
  { symbol: "ORLY", display: "O'Reilly" },
  { symbol: "AZO", display: "AutoZone" },
  { symbol: "TSCO", display: "Tractor Supply" },
  { symbol: "FND", display: "Floor & Decor" },

  // ── Food, drink & restaurants ────────────────────────────────────────────
  { symbol: "SBUX", display: "Starbucks" },
  { symbol: "MCD", display: "McDonald's" },
  { symbol: "CMG", display: "Chipotle" },
  { symbol: "YUM", display: "Yum! Brands", aliases: ["tacobell", "kfc", "pizzahut"] },
  { symbol: "QSR", display: "Restaurant Brands", aliases: ["burgerking", "timhortons", "popeyes"] },
  { symbol: "WEN", display: "Wendy's" },
  { symbol: "DPZ", display: "Domino's" },
  { symbol: "PZZA", display: "Papa John's" },
  { symbol: "DRI", display: "Darden", aliases: ["olivegarden", "longhorn"] },
  { symbol: "TXRH", display: "Texas Roadhouse" },
  { symbol: "CAKE", display: "Cheesecake Factory" },
  { symbol: "SHAK", display: "Shake Shack" },
  { symbol: "WING", display: "Wingstop" },
  { symbol: "DNUT", display: "Krispy Kreme" },
  { symbol: "KO", display: "Coca-Cola", aliases: ["coke", "sprite", "dasani"] },
  { symbol: "PEP", display: "PepsiCo", aliases: ["pepsi", "gatorade", "doritos", "lays", "quaker", "fritolay", "tostitos"] },
  { symbol: "KDP", display: "Keurig Dr Pepper", aliases: ["keurig", "drpepper"] },
  { symbol: "MNST", display: "Monster Beverage", aliases: ["monsterenergy"] },
  { symbol: "CELH", display: "Celsius" },
  { symbol: "STZ", display: "Constellation Brands", aliases: ["corona", "modelo"] },
  { symbol: "TAP", display: "Molson Coors", aliases: ["coors", "millerlite"] },
  { symbol: "KHC", display: "Kraft Heinz", aliases: ["kraft", "heinz", "caprisun", "lunchables"] },
  { symbol: "GIS", display: "General Mills", aliases: ["cheerios", "pillsbury", "yoplait", "annies"] },
  { symbol: "K", display: "Kellanova", aliases: ["kelloggs", "poptarts", "pringles"] },
  { symbol: "POST", display: "Post Holdings" },
  { symbol: "CPB", display: "Campbell's", aliases: ["goldfish", "pepperidgefarm"] },
  { symbol: "CAG", display: "Conagra", aliases: ["healthychoice", "marierecallenders"] },
  { symbol: "SJM", display: "Smucker", aliases: ["jif", "folgers", "uncrustables"] },
  { symbol: "HSY", display: "Hershey", aliases: ["reeses", "kisses"] },
  { symbol: "MDLZ", display: "Mondelez", aliases: ["oreo", "cadbury", "ritz", "belvita"] },
  { symbol: "MKC", display: "McCormick" },
  { symbol: "HRL", display: "Hormel", aliases: ["spam", "skippy"] },
  { symbol: "TSN", display: "Tyson Foods", aliases: ["tyson"] },
  { symbol: "NSRGY", display: "Nestlé", aliases: ["nestle", "gerber", "purina", "nespresso"] },

  // ── Household & personal care ────────────────────────────────────────────
  { symbol: "PG", display: "Procter & Gamble", aliases: ["tide", "pampers", "gillette", "crest", "dawn", "bounty", "charmin", "olay"] },
  { symbol: "KMB", display: "Kimberly-Clark", aliases: ["huggies", "kleenex", "cottonelle", "pullups"] },
  { symbol: "CL", display: "Colgate-Palmolive", aliases: ["colgate", "palmolive", "hillspetnutrition"] },
  { symbol: "CHD", display: "Church & Dwight", aliases: ["armandhammer", "oxiclean", "waterpik"] },
  { symbol: "CLX", display: "Clorox", aliases: ["burtsbees", "hiddenvalley", "glad"] },
  { symbol: "EL", display: "Estée Lauder", aliases: ["esteelauder", "clinique", "maccosmetics", "bobbibrown"] },
  { symbol: "COTY", display: "Coty" },
  { symbol: "ELF", display: "e.l.f. Beauty", aliases: ["elfbeauty", "elfcosmetics"] },
  { symbol: "UL", display: "Unilever", aliases: ["dove", "benandjerrys", "hellmanns", "vaseline"] },

  // ── Health ───────────────────────────────────────────────────────────────
  { symbol: "JNJ", display: "Johnson & Johnson" },
  { symbol: "KVUE", display: "Kenvue", aliases: ["tylenol", "neutrogena", "listerine", "bandaid", "aveeno"] },
  { symbol: "PFE", display: "Pfizer" },
  { symbol: "MRK", display: "Merck" },
  { symbol: "ABBV", display: "AbbVie" },
  { symbol: "LLY", display: "Eli Lilly", aliases: ["lilly", "zepbound", "mounjaro"] },
  { symbol: "BMY", display: "Bristol Myers Squibb" },
  { symbol: "AMGN", display: "Amgen" },
  { symbol: "NVO", display: "Novo Nordisk", aliases: ["ozempic", "wegovy"] },
  { symbol: "UNH", display: "UnitedHealth" },
  { symbol: "CI", display: "Cigna" },
  { symbol: "ELV", display: "Elevance" },
  { symbol: "HUM", display: "Humana" },
  { symbol: "HCA", display: "HCA Healthcare" },
  { symbol: "MDT", display: "Medtronic" },
  { symbol: "ABT", display: "Abbott", aliases: ["similac", "pedialyte", "ensure"] },
  { symbol: "ISRG", display: "Intuitive Surgical" },
  { symbol: "ZTS", display: "Zoetis" },
  { symbol: "DXCM", display: "Dexcom" },
  { symbol: "HIMS", display: "Hims & Hers", aliases: ["hims", "hers"] },
  { symbol: "WW", display: "WW International", aliases: ["weightwatchers"] },
  { symbol: "PTON", display: "Peloton" },
  { symbol: "PLNT", display: "Planet Fitness" },

  // ── Money ────────────────────────────────────────────────────────────────
  { symbol: "JPM", display: "JPMorgan Chase", aliases: ["chase", "jpmorgan"] },
  { symbol: "BAC", display: "Bank of America" },
  { symbol: "WFC", display: "Wells Fargo" },
  { symbol: "C", display: "Citigroup", aliases: ["citi", "citibank"] },
  { symbol: "GS", display: "Goldman Sachs" },
  { symbol: "MS", display: "Morgan Stanley" },
  { symbol: "AXP", display: "American Express", aliases: ["amex"] },
  { symbol: "V", display: "Visa" },
  { symbol: "MA", display: "Mastercard" },
  { symbol: "COF", display: "Capital One" },
  { symbol: "DFS", display: "Discover" },
  { symbol: "SCHW", display: "Charles Schwab", aliases: ["schwab"] },
  { symbol: "BRK.B", display: "Berkshire Hathaway", aliases: ["berkshire", "brkb"] },
  { symbol: "BLK", display: "BlackRock" },
  { symbol: "INTU", display: "Intuit", aliases: ["turbotax", "quickbooks", "mailchimp", "creditkarma"] },
  { symbol: "ALLY", display: "Ally" },
  { symbol: "PGR", display: "Progressive" },
  { symbol: "ALL", display: "Allstate" },
  { symbol: "TRV", display: "Travelers" },
  { symbol: "CB", display: "Chubb" },
  { symbol: "MET", display: "MetLife" },
  { symbol: "PRU", display: "Prudential" },

  // ── Travel, cars, industry ───────────────────────────────────────────────
  { symbol: "DAL", display: "Delta" },
  { symbol: "UAL", display: "United Airlines" },
  { symbol: "AAL", display: "American Airlines" },
  { symbol: "LUV", display: "Southwest" },
  { symbol: "JBLU", display: "JetBlue" },
  { symbol: "MAR", display: "Marriott" },
  { symbol: "HLT", display: "Hilton" },
  { symbol: "H", display: "Hyatt" },
  { symbol: "BKNG", display: "Booking Holdings", aliases: ["bookingcom", "priceline", "kayak", "opentable"] },
  { symbol: "EXPE", display: "Expedia", aliases: ["vrbo"] },
  { symbol: "RCL", display: "Royal Caribbean" },
  { symbol: "CCL", display: "Carnival" },
  { symbol: "NCLH", display: "Norwegian Cruise Line" },
  { symbol: "F", display: "Ford" },
  { symbol: "GM", display: "General Motors", aliases: ["chevrolet", "chevy", "cadillac", "gmc", "buick"] },
  { symbol: "RIVN", display: "Rivian" },
  { symbol: "LCID", display: "Lucid" },
  { symbol: "TM", display: "Toyota", aliases: ["lexus"] },
  { symbol: "HMC", display: "Honda", aliases: ["acura"] },
  { symbol: "STLA", display: "Stellantis", aliases: ["jeep", "chrysler", "dodge", "ram"] },
  { symbol: "BA", display: "Boeing" },
  { symbol: "CAT", display: "Caterpillar" },
  { symbol: "DE", display: "Deere", aliases: ["johndeere"] },
  { symbol: "GE", display: "GE Aerospace" },
  { symbol: "HON", display: "Honeywell" },
  { symbol: "MMM", display: "3M", aliases: ["postit", "scotchtape", "commandhooks"] },
  { symbol: "LMT", display: "Lockheed Martin" },
  { symbol: "UPS", display: "UPS" },
  { symbol: "FDX", display: "FedEx" },

  // ── Media & telecom ──────────────────────────────────────────────────────
  { symbol: "CMCSA", display: "Comcast", aliases: ["xfinity", "nbc", "peacock", "universal"] },
  { symbol: "WBD", display: "Warner Bros. Discovery", aliases: ["warnerbros", "hbo", "hbomax", "discovery"] },
  { symbol: "T", display: "AT&T", aliases: ["att"] },
  { symbol: "VZ", display: "Verizon" },
  { symbol: "TMUS", display: "T-Mobile" },
  { symbol: "CHTR", display: "Charter", aliases: ["spectrum"] },
  { symbol: "LYV", display: "Live Nation", aliases: ["ticketmaster"] },

  // ── Energy & utilities ───────────────────────────────────────────────────
  { symbol: "XOM", display: "Exxon Mobil", aliases: ["exxon", "mobil"] },
  { symbol: "CVX", display: "Chevron" },
  { symbol: "COP", display: "ConocoPhillips" },
  { symbol: "NEE", display: "NextEra" },
  { symbol: "DUK", display: "Duke Energy" },
  { symbol: "SO", display: "Southern Company" },
  { symbol: "ENPH", display: "Enphase" },
  { symbol: "FSLR", display: "First Solar" },

  // ── Homes ────────────────────────────────────────────────────────────────
  { symbol: "DHI", display: "D.R. Horton" },
  { symbol: "LEN", display: "Lennar" },
  { symbol: "ZG", display: "Zillow" },
  { symbol: "OPEN", display: "Opendoor" },
  { symbol: "CSGP", display: "CoStar", aliases: ["apartmentscom", "homescom"] },

  // ── The funds people say out loud ────────────────────────────────────────
  // A watchlist is the one place a mom is as likely to type "S&P 500" as a
  // company. nameKey("S&P 500") is "sp500", so all three spellings land here.
  { symbol: "SPY", display: "S&P 500", aliases: ["sp500", "sandp500", "spx"] },
  { symbol: "VOO", display: "Vanguard S&P 500" },
  { symbol: "VTI", display: "Vanguard Total Stock Market" },
  { symbol: "QQQ", display: "Nasdaq 100", aliases: ["nasdaq", "nasdaq100"] },
  { symbol: "DIA", display: "Dow Jones", aliases: ["dowjones", "dow"] },
];

interface PrivateEntry {
  /** How Kin names it back to her. */
  display: string;
  /** The listed parent, when there is one she can actually follow. */
  parent?: { name: string; symbol: string };
  /** Overrides the default "privately held" sentence. */
  /** A complete replacement sentence, for the cases "privately held" gets wrong. */
  note?: string;
}

/**
 * Companies that are NOT listed, keyed the same way. This list is the whole
 * reason the feature read as broken rather than fiddly: "SEPHORA" is not a typo
 * and no amount of fuzzy matching turns it into a ticker. LVMH owns it, LVMH is
 * the thing you can actually follow, and saying so is the entire fix.
 */
const NOT_PUBLIC: Record<string, PrivateEntry> = {
  sephora: { display: "Sephora", parent: { name: "LVMH", symbol: "LVMUY" } },
  louisvuitton: { display: "Louis Vuitton", parent: { name: "LVMH", symbol: "LVMUY" } },
  zara: { display: "Zara", parent: { name: "Inditex", symbol: "IDEXY" } },
  hm: { display: "H&M", parent: { name: "H&M", symbol: "HNNMY" } },
  handm: { display: "H&M", parent: { name: "H&M", symbol: "HNNMY" } },
  tiktok: { display: "TikTok", note: "TikTok's owner, ByteDance, is privately held" },
  ikea: { display: "IKEA" },
  lego: { display: "LEGO" },
  traderjoes: { display: "Trader Joe's" },
  aldi: { display: "Aldi" },
  publix: { display: "Publix", note: "Publix is employee-owned and isn't traded on an exchange" },
  wegmans: { display: "Wegmans" },
  heb: { display: "H-E-B" },
  chickfila: { display: "Chick-fil-A" },
  innout: { display: "In-N-Out" },
  innoutburger: { display: "In-N-Out" },
  hobbylobby: { display: "Hobby Lobby" },
  patagonia: { display: "Patagonia" },
  hallmark: { display: "Hallmark" },
  mars: { display: "Mars", note: "Mars (M&M's, Snickers, Pedigree) is family-owned and privately held" },
  spacex: { display: "SpaceX" },
  openai: { display: "OpenAI" },
  anthropic: { display: "Anthropic" },
  stripe: { display: "Stripe" },
  shein: { display: "SHEIN" },
  fidelity: { display: "Fidelity" },
  statefarm: { display: "State Farm", note: "State Farm is a mutual company, owned by its policyholders" },
  usaa: { display: "USAA", note: "USAA is member-owned" },
  libertymutual: { display: "Liberty Mutual", note: "Liberty Mutual is a mutual company, owned by its policyholders" },
  vanguard: {
    display: "Vanguard",
    note: "Vanguard is owned by its own funds, so the company itself has no ticker. Add VOO if you want its S&P 500 fund",
  },
};

// ⚠️ A Map, not the object literal above, and that is not style. The key comes
// from whatever she typed, so `NOT_PUBLIC[key]` answers "constructor" with
// Object's constructor — truthy, no `display`, and the refusal renders
// "undefined is privately held, so there's no ticker to follow."
const NOT_PUBLIC_BY_KEY = new Map<string, PrivateEntry>(Object.entries(NOT_PUBLIC));

const BY_KEY = new Map<string, { ticker: ResolvedTicker; alias: boolean }>();
const BY_SYMBOL = new Map<string, string>();
for (const c of COMPANIES) {
  const resolved: ResolvedTicker = { symbol: c.symbol, display: c.display };
  BY_SYMBOL.set(c.symbol, c.display);
  const keys: Array<[string, boolean]> = [[nameKey(c.display), false], ...(c.aliases ?? []).map((a): [string, boolean] => [nameKey(a), true])];
  for (const [key, alias] of keys) {
    // First definition wins, so an alias can never quietly shadow a real
    // company name that appears later in the table.
    if (key && !BY_KEY.has(key)) BY_KEY.set(key, { ticker: resolved, alias });
  }
}

/** Written the way a ticker is written: capitals, optional $ and class suffix. */
const TYPED_AS_TICKER = /^\$?[A-Z]{1,5}(\.[A-Z]{1,2})?$/;

/** The company name behind a symbol, or the symbol itself when we don't know it. */
export function displayForSymbol(symbol: string): string {
  return BY_SYMBOL.get(symbol.toUpperCase()) ?? symbol.toUpperCase();
}

/** What the chip reads. "Apple (AAPL)", or just "ACME" when we have no name. */
export function chipLabel(t: ResolvedTicker): string {
  return t.display && t.display !== t.symbol ? `${t.display} (${t.symbol})` : t.symbol;
}

function refusePrivate(entry: PrivateEntry): TickerResolution {
  if (entry.parent) {
    return {
      ok: false,
      message: `${entry.display} isn't traded on its own. It's part of ${entry.parent.name}, so add ${entry.parent.symbol} if you want to follow the parent company.`,
    };
  }
  // `note` is the whole sentence when the default one would be wrong — Publix
  // is employee-owned, State Farm is a mutual, Vanguard is owned by its funds.
  // "privately held" is close enough to be believed and wrong enough to matter.
  if (entry.note) return { ok: false, message: `${entry.note}.` };
  return { ok: false, message: `${entry.display} is privately held, so there's no ticker to follow.` };
}

/**
 * The one entry point. Takes whatever she typed and returns either the symbol
 * to save or the sentence to show her.
 *
 * Order matters and each step is load-bearing:
 *   1. Known-private FIRST. "lego" and "ikea" are 4 letters and would sail
 *      straight through the symbol-shape fallback otherwise, saving a string
 *      that can never come back with a price — the exact bug being fixed.
 *   2. Company names and aliases next, so "visa" and "nike" resolve to V and
 *      NKE rather than being read as the 4-letter symbols VISA and NIKE.
 *   3. Symbol shape last, as the permissive fallback described in the header.
 */
export function resolveTickerInput(raw: string): TickerResolution {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, message: "" };

  const key = nameKey(trimmed);
  if (!key) {
    return { ok: false, message: "Hmm, Kin couldn't make a ticker out of that one. Try the company name, or the symbol if you know it." };
  }

  const priv = NOT_PUBLIC_BY_KEY.get(key);
  if (priv) return refusePrivate(priv);

  const typedAsTicker = TYPED_AS_TICKER.test(trimmed);
  const known = BY_KEY.get(key);
  // Capitals skip nicknames ("COKE" is the ticker COKE), never real names.
  if (known && !(known.alias && typedAsTicker)) {
    return { ok: true, symbol: known.ticker.symbol, display: known.ticker.display };
  }

  const candidate = symbolCandidate(trimmed);
  // A space means she typed words, not a symbol — "home depot" must never
  // become HOMEDEPOT, and stripping the space would hide that it was a name.
  if (!/\s/.test(trimmed) && SYMBOL_SHAPE.test(candidate)) {
    // A ticker Kin knows, in any case ("aapl").
    if (BY_SYMBOL.has(candidate)) return { ok: true, symbol: candidate, display: displayForSymbol(candidate) };
    // One Kin doesn't know: only when she wrote it as a ticker.
    if (typedAsTicker) return { ok: true, symbol: candidate, display: candidate };
    return {
      ok: false,
      message: `Hmm, Kin couldn't find "${trimmed}". If it's a ticker symbol, type it in capitals, like ${candidate}.`,
    };
  }

  return {
    ok: false,
    message: `Hmm, Kin couldn't find "${trimmed}" on the market. Is it part of a bigger company? Try the parent company, or the ticker symbol if you know it.`,
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Stored shape — issue #440 point 5, no schema change
// ──────────────────────────────────────────────────────────────────────────────

/**
 * `briefing_prefs.stocks.tickers` is JSONB and held plain strings before this.
 * Going forward it holds `{symbol, display}` objects; legacy plain strings are
 * tolerated on read, forever, by both this file and the dispatcher's own reader
 * (`supabase/functions/dispatch-notifications/stockTickers.ts`).
 *
 * ⚠️ Those two readers are deliberately NOT the same code and must not be
 * merged. This one has the company table and can turn "AMAZON" into AMZN. The
 * dispatcher's has no table on purpose — it answers a narrower question ("is
 * this a usable symbol, yes or no") so that a table edit here can never change
 * what a morning briefing sends.
 */
export type StoredTicker = string | { symbol?: unknown; display?: unknown };

export type MigratedEntry =
  | { kind: "ok"; ticker: ResolvedTicker }
  /** A stored string we cannot resolve. Kept, never rewritten, shown with `message`. */
  | { kind: "problem"; raw: string; message: string };

/**
 * Re-resolves everything already saved (issue #440 point 3).
 *
 * Three rules, and the third is the one that matters:
 *   - An object is already migrated: kept as-is.
 *   - A string that resolves is upgraded in place ("AMAZON" → Amazon/AMZN).
 *     This is what silently fixes her existing watchlist.
 *   - A string that does NOT resolve is kept EXACTLY as stored and surfaced.
 *     Deleting it would be the same silent failure in a new costume — the row
 *     would vanish from a list she never asked us to edit. It stays put and the
 *     prompt comes back every time the Builder opens until she deals with it,
 *     which is the behaviour the issue asked for.
 */
export function migrateStoredTickers(stored: unknown): { entries: MigratedEntry[]; changed: boolean } {
  if (!Array.isArray(stored)) return { entries: [], changed: !!stored };

  const entries: MigratedEntry[] = [];
  const seen = new Set<string>();
  let changed = false;

  for (const item of stored as StoredTicker[]) {
    if (item && typeof item === "object") {
      const symbol = typeof item.symbol === "string" ? item.symbol.trim().toUpperCase() : "";
      if (!symbol) { changed = true; continue; } // malformed object — nothing to show or keep
      if (seen.has(symbol)) { changed = true; continue; }
      seen.add(symbol);
      const display = typeof item.display === "string" && item.display.trim() ? item.display.trim() : displayForSymbol(symbol);
      if (display !== item.display || symbol !== item.symbol) changed = true;
      entries.push({ kind: "ok", ticker: { symbol, display } });
      continue;
    }

    if (typeof item !== "string") { changed = true; continue; }
    const res = resolveTickerInput(item);
    if (res.ok === false) {
      entries.push({ kind: "problem", raw: item, message: res.message });
      continue;
    }
    if (seen.has(res.symbol)) { changed = true; continue; }
    seen.add(res.symbol);
    changed = true; // a string became an object, even when the symbol is unchanged
    entries.push({ kind: "ok", ticker: { symbol: res.symbol, display: res.display } });
  }

  return { entries, changed };
}

/** Back to the JSONB shape, problems preserved verbatim. */
export function entriesToStored(entries: MigratedEntry[]): StoredTicker[] {
  return entries.map((e) => (e.kind === "ok" ? { symbol: e.ticker.symbol, display: e.ticker.display } : e.raw));
}
